import { getBrandLogoUrl, getBrandSignatureUrl, getOrgConfig } from '@api/organization';
import {
    amountInWords,
    groupTaxByHsn,
    gstStateLabel,
    gstTreatment,
    splitGst,
} from '@diagon-alley/common';
import ClauseText from '@components/ui/ClauseText';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo } from 'react';

import { getOrganizationIdFromStorage } from '../procurementOrders/formComponents';
import { formatDateDisplay } from '../procurementOrders/shared';

import classes from './ProductionOrderPreview.module.css';
import { formatCount } from '@utils/currency';

export type PreviewLine = {
    productId: string;
    productName?: string | null;
    /** The code a person quotes. Falls back to the id, as the PDF does. */
    productSku?: string | null;
    /**
     * HSN/SAC, when the picker knew it.
     *
     * Blank is normal rather than wrong: only the vendor-mapped picker carries it, so a line
     * chosen off the full catalogue shows an empty cell here. The document itself reads the
     * code off the product at render time, so the PDF fills what this cannot.
     */
    hsnCode?: string | null;
    /*
     * Numbers OR the half-typed text behind them, because this renders while somebody types.
     * '241.' is a real intermediate state of a number input and has to survive to the preview,
     * or a decimal rate cannot be entered at all.
     */
    quantity: number | string;
    unitPrice: number | string;
    /** Blank is "no rate stated", which prints as a dash. It is not the exempt 0% slab. */
    gstRate: number | string;
    dueDate?: string;
};

export type ProductionOrderPreviewProps = {
    code: string;
    status: string;
    /** Absent for an in-house run, which is how the page says it is not a transaction. */
    currency: string | null;
    orderDate: string;
    expectedCompletionDate: string;
    manufacturerName: string;
    manufacturerAddress: string;
    manufacturerGstin: string;
    billToName: string;
    billToAddress: string;
    billToGstin: string;
    shipToName: string;
    shipToAddress: string;
    shipToGstin: string;
    /** Place of supply. With the manufacturer's own state, decides IGST against CGST + SGST. */
    shipToStateCode: string | null;
    lines: PreviewLine[];
    charges: Array<{ label: string; amount: number | '' }>;
    notes: string;
    terms: string;
};

const num = (value: number | string) => Number(value) || 0;

/**
 * Whether a rate was actually stated, as against blank or half-typed.
 *
 * The document turns on this: a stated 0% is the exempt slab and prints as 0%, while nothing
 * stated prints as a dash. A lone '.' mid-keystroke is not a statement of anything, so it reads
 * as unstated rather than as an exemption the brand never claimed.
 */
const stated = (value: number | string) => value !== '' && Number.isFinite(Number(value));

/**
 * Money as the DOCUMENT prints it, which is deliberately not how the app prints it.
 *
 * en-IN groups in lakhs — 2,89,800.00 — and every Indian tax document a counterparty compares
 * this against is grouped that way. The app's own `formatMoney` uses en-US for grids, by a
 * product convention noted where it is defined; that convention stops at the edge of a document.
 *
 * So this pane formats money unlike every other screen, on purpose: it is a preview of a page
 * somebody will receive, and it has to match `formatDocumentMoney` on the server rather than the
 * grid beside it.
 */
const amount = (value: number) =>
    formatCount(value, { decimals: 2 });

/*
 * INSIDE THE TABLES THE CURRENCY IS NOISE, and expensive noise: repeating "INR" on every cell of
 * four money columns is what pushed the ten-column grid past the width of the sheet. It is
 * stated where it is actually read - the totals block and the amount in words - and the columns
 * carry figures. Same split as the renderer's `formatDocumentAmount` / `formatDocumentMoney`.
 */
const money = (value: number, currency: string) => `${currency} ${amount(value)}`;

const lineHasContent = (line: PreviewLine) =>
    Boolean(line.productId || line.quantity || line.unitPrice);

const taxableValue = (line: PreviewLine) => num(line.quantity) * num(line.unitPrice);

/** Same day, ignoring any time part — these are date-only strings from the form. */
const sameDay = (a?: string | null, b?: string | null) =>
    Boolean(a) && Boolean(b) && a!.slice(0, 10) === b!.slice(0, 10);
const lineTax = (line: PreviewLine) => (taxableValue(line) * num(line.gstRate)) / 100;

/** A party block, in the shape `renderPartyBlock` gives the document. */
function Party({
    label,
    name,
    address,
    gstin,
}: {
    label: string;
    name: string;
    address: string;
    gstin: string;
}) {
    return (
        <>
            <div className={classes.label}>{label}</div>
            {name || address || gstin ? (
                <>
                    {name && <div className={classes.partyName}>{name}</div>}
                    {address && <div className={classes.partyLine}>{address}</div>}
                    {gstin && <div className={classes.partyLine}>GSTIN: {gstin}</div>}
                </>
            ) : (
                <div className={classes.partyEmpty}>Not provided</div>
            )}
        </>
    );
}

/**
 * The production order as the manufacturer will receive it, while it is being written.
 *
 * IT MIRRORS THE PDF, and that is the whole requirement rather than a nicety — this is the only
 * check anybody gets before a document leaves the building, so a preview that renders a
 * different shape than the renderer would be worse than no preview at all. The ruled grid, the
 * column set, dash-for-unstated-GST, lakh grouping, the tax split and the drop-everything-for-
 * in-house rule are all the same rules, expressed here in React and there in HTML. When one
 * changes the other has to.
 *
 * THE ARITHMETIC IS NOT REIMPLEMENTED. `gstTreatment`, `splitGst` and `amountInWords` are
 * imported from the shared package — the same functions the renderer calls — so the two cannot
 * disagree about a tax or a total. Only the markup differs, which is the only thing that should.
 *
 * NOT the purchase-order DocumentPreview. That one is shaped around delivery and payment terms;
 * this is shaped around what to make and by when, with money added beside it.
 */
const ProductionOrderPreview: FC<ProductionOrderPreviewProps> = (p) => {
    const organizationId = useMemo(() => getOrganizationIdFromStorage(), []);

    const orgQuery = useQuery({
        queryKey: ['org-config', organizationId],
        queryFn: () => getOrgConfig(organizationId as string),
        enabled: !!organizationId,
    });

    // A signed link here, inlined base64 there: a preview can survive a broken image, and a
    // headless render waiting on a network fetch cannot.
    const logoQuery = useQuery({ queryKey: ['brand-logo-url'], queryFn: getBrandLogoUrl });
    const signatureQuery = useQuery({
        queryKey: ['brand-signature-url'],
        queryFn: getBrandSignatureUrl,
    });

    const org = orgQuery.data;
    const visible = p.lines.filter(lineHasContent);
    const priced = Boolean(p.currency);
    const currency = p.currency ?? '';

    const subtotal = visible.reduce((sum, line) => sum + taxableValue(line), 0);
    const tax = visible.reduce((sum, line) => sum + lineTax(line), 0);
    const chargesTotal = p.charges.reduce((sum, charge) => sum + num(charge.amount), 0);
    const total = subtotal + tax + chargesTotal;
    const totalUnits = visible.reduce((sum, line) => sum + num(line.quantity), 0);

    /*
     * Supplier against destination, never the issuing brand against the destination — a Haryana
     * brand buying from a Gujarat manufacturer for delivery into Haryana is interstate, because
     * the goods cross a border on the way.
     */
    const treatment = gstTreatment(p.manufacturerGstin, p.shipToStateCode);
    const split = splitGst(tax, null, treatment);
    const placeOfSupply = gstStateLabel(p.shipToStateCode);

    /*
     * The Due column only earns its place when a line was negotiated to a different date from
     * the order's. Otherwise it is a header over empty cells, in a table with no width to spare.
     */
    const showDue = visible.some(
        (line) => line.dueDate && !sameDay(line.dueDate, p.expectedCompletionDate),
    );

    // Same roll-up the renderer uses, so the summary here is the summary that prints.
    const hsnGroups = priced ? groupTaxByHsn(visible) : [];

    const brandLines = [
        org?.gstin && `GSTIN: ${org.gstin}`,
        org?.address,
        org?.phone && `Mobile: ${org.phone}`,
        org?.website && `Website: ${org.website}`,
    ].filter(Boolean) as string[];

    const columnCount = (priced ? 9 : 5) + (showDue ? 1 : 0);

    return (
        <div className={classes.sheet}>
            <div className={classes.docTitle}>PRODUCTION ORDER</div>

            <div className={classes.headGrid}>
                <div className={classes.brandCell}>
                    {logoQuery.data?.url && (
                        <img src={logoQuery.data.url} alt="" className={classes.brandLogo} />
                    )}
                    <div className={classes.brandName}>{org?.name ?? '—'}</div>
                    {brandLines.map((line) => (
                        <div className={classes.partyLine} key={line}>
                            {line}
                        </div>
                    ))}
                </div>

                <div className={classes.metaCell}>
                    <div className={classes.metaSplit}>
                        <div className={classes.metaBox}>
                            <div className={classes.label}>Production Order #</div>
                            <div className={classes.metaValue}>{p.code || 'Auto-generated'}</div>
                        </div>
                        <div className={classes.metaBoxLast}>
                            <div className={classes.label}>Order Date</div>
                            <div className={classes.metaValue}>
                                {p.orderDate ? formatDateDisplay(p.orderDate) : '—'}
                            </div>
                        </div>
                    </div>
                    {placeOfSupply && (
                        <div className={classes.metaBoxWide}>
                            <div className={classes.label}>Place of Supply</div>
                            <div className={classes.metaValue}>{placeOfSupply}</div>
                        </div>
                    )}
                    <div className={classes.metaBoxOpen}>
                        <Party
                            label="Dispatch To"
                            name={p.shipToName}
                            address={p.shipToAddress}
                            gstin={p.shipToGstin}
                        />
                    </div>
                </div>
            </div>

            <div className={classes.partyGrid}>
                <div className={classes.partyCell}>
                    <Party
                        label="Manufacturer"
                        name={p.manufacturerName}
                        address={p.manufacturerAddress}
                        gstin={p.manufacturerGstin}
                    />
                </div>
                <div className={classes.partyCellLast}>
                    {/*
                     * An in-house run has nobody to bill, so the cell carries the date the site
                     * is held to instead — the same swap the renderer makes.
                     */}
                    {priced ? (
                        <Party
                            label="Bill To"
                            name={p.billToName}
                            address={p.billToAddress}
                            gstin={p.billToGstin}
                        />
                    ) : (
                        <>
                            <div className={classes.label}>Required By</div>
                            <div className={classes.metaValue}>
                                {p.expectedCompletionDate
                                    ? formatDateDisplay(p.expectedCompletionDate)
                                    : '—'}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <table className={classes.table}>
                <thead>
                    <tr>
                        <th className={classes.thNum}>#</th>
                        <th className={classes.thItem}>Item</th>
                        <th className={classes.th}>SKU</th>
                        <th className={classes.th}>HSN/SAC</th>
                        <th className={classes.thRight}>Qty</th>
                        {priced && (
                            <>
                                <th className={classes.thRight}>Rate / Item</th>
                                <th className={classes.thRight}>Taxable Value</th>
                                <th className={classes.thRight}>Tax Amount</th>
                                <th className={classes.thRight}>Amount</th>
                            </>
                        )}
                        {showDue && <th className={classes.thRight}>Due</th>}
                    </tr>
                </thead>
                <tbody>
                    {visible.length === 0 && (
                        <tr>
                            <td className={classes.empty} colSpan={columnCount}>
                                No line items yet.
                            </td>
                        </tr>
                    )}
                    {visible.map((line, index) => (
                        <tr key={`${line.productId}-${index}`}>
                            <td className={classes.tdNum}>{index + 1}</td>
                            <td className={classes.tdItem}>
                                {line.productName || line.productId}
                            </td>
                            {/* The product code, not the barcode - what both sides of this
                                document call the item. Mirrors the emailed PDF's column. */}
                            <td className={classes.tdMuted}>{line.productId}</td>
                            <td className={classes.tdMuted}>{line.hsnCode ?? ''}</td>
                            <td className={classes.tdRight}>
                                {formatCount(num(line.quantity))} pcs
                            </td>
                            {priced && (
                                <>
                                    <td className={classes.tdRight}>
                                        {amount(num(line.unitPrice))}
                                    </td>
                                    <td className={classes.tdRight}>
                                        {amount(taxableValue(line))}
                                    </td>
                                    {/*
                                     * A dash, never 0%. "No rate stated" and "the exempt 0%
                                     * slab" are different claims, and the document must not
                                     * make the second on the brand's behalf.
                                     */}
                                    <td className={classes.tdRight}>
                                        {stated(line.gstRate)
                                            ? `${amount(lineTax(line))} (${Number(line.gstRate)}%)`
                                            : '—'}
                                    </td>
                                    <td className={classes.tdRightStrong}>
                                        {amount(taxableValue(line) + lineTax(line))}
                                    </td>
                                </>
                            )}
                            {showDue && (
                                <td className={classes.tdMutedRight}>
                                    {line.dueDate && !sameDay(line.dueDate, p.expectedCompletionDate)
                                        ? formatDateDisplay(line.dueDate)
                                        : ''}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className={classes.summaryRow}>
                <div className={classes.summaryLeft}>
                    Total Items / Qty : {visible.length} / {formatCount(totalUnits)} pcs
                    <br />
                    Required by{' '}
                    {p.expectedCompletionDate ? formatDateDisplay(p.expectedCompletionDate) : '—'}
                </div>
                {priced && (
                    <div className={classes.totals}>
                        <div className={classes.totalRow}>
                            <span>Taxable Amount</span>
                            <span className={classes.totalValue}>{money(subtotal, currency)}</span>
                        </div>
                        {split.treatment === 'INTRASTATE' && (
                            <>
                                <div className={classes.totalRow}>
                                    <span>CGST</span>
                                    <span className={classes.totalValue}>
                                        {money(split.cgst, currency)}
                                    </span>
                                </div>
                                <div className={classes.totalRow}>
                                    <span>SGST</span>
                                    <span className={classes.totalValue}>
                                        {money(split.sgst, currency)}
                                    </span>
                                </div>
                            </>
                        )}
                        {split.treatment === 'INTERSTATE' && (
                            <div className={classes.totalRow}>
                                <span>IGST</span>
                                <span className={classes.totalValue}>
                                    {money(split.igst, currency)}
                                </span>
                            </div>
                        )}
                        {/*
                         * No GSTIN or no place of supply means no honest split to show, so the
                         * document reports one undifferentiated Tax rather than guessing.
                         */}
                        {split.treatment === 'UNKNOWN' && (
                            <div className={classes.totalRow}>
                                <span>Tax</span>
                                <span className={classes.totalValue}>{money(tax, currency)}</span>
                            </div>
                        )}
                        {p.charges
                            .filter((charge) => charge.label || charge.amount)
                            .map((charge, index) => (
                                <div className={classes.totalRow} key={`${charge.label}-${index}`}>
                                    <span>{charge.label || 'Charge'}</span>
                                    <span className={classes.totalValue}>
                                        {money(num(charge.amount), currency)}
                                    </span>
                                </div>
                            ))}
                        <div className={classes.totalRowStrong}>
                            <span>Total</span>
                            <span className={classes.totalValue}>{money(total, currency)}</span>
                        </div>
                    </div>
                )}
            </div>

            {priced && (
                <div className={classes.inWords}>
                    Total amount (in words): {amountInWords(total, currency)}
                </div>
            )}

            {hsnGroups.length > 0 && (
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th className={classes.th}>HSN/SAC</th>
                            <th className={classes.thRight}>Taxable Value</th>
                            {/*
                              * Names only the tax we have actually determined. Undetermined
                              * reads "Tax", matching the totals block above rather than
                              * asserting a split on the same page that declines to.
                              */}
                            <th className={classes.thRight} colSpan={2}>
                                {split.treatment === 'INTERSTATE'
                                    ? 'Integrated Tax'
                                    : split.treatment === 'INTRASTATE'
                                      ? 'Central Tax & State Tax'
                                      : 'Tax'}
                            </th>
                            <th className={classes.thRight}>Total Tax Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {hsnGroups.map((group) => {
                            const groupSplit = splitGst(group.tax, group.rate, treatment);
                            return (
                                <tr key={`${group.hsn}-${group.rate}`}>
                                    <td className={classes.td}>{group.hsn}</td>
                                    <td className={classes.tdRight}>
                                        {amount(group.taxable)}
                                    </td>
                                    {groupSplit.treatment === 'INTRASTATE' ? (
                                        <>
                                            <td className={classes.tdRight}>
                                                {group.rate / 2}% + {group.rate / 2}%
                                            </td>
                                            <td className={classes.tdRight}>
                                                {amount(groupSplit.cgst)} +{' '}
                                                {amount(groupSplit.sgst)}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className={classes.tdRight}>{group.rate}%</td>
                                            <td className={classes.tdRight}>
                                                {amount(group.tax)}
                                            </td>
                                        </>
                                    )}
                                    <td className={classes.tdRight}>
                                        {amount(group.tax)}
                                    </td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td className={classes.tdStrong}>TOTAL</td>
                            <td className={classes.tdRightStrong}>
                                {amount(hsnGroups.reduce((sum, group) => sum + group.taxable, 0))}
                            </td>
                            <td className={classes.td} />
                            <td className={classes.tdRightStrong}>
                                {amount(hsnGroups.reduce((sum, group) => sum + group.tax, 0))}
                            </td>
                            <td className={classes.tdRightStrong}>
                                {amount(hsnGroups.reduce((sum, group) => sum + group.tax, 0))}
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}

            {p.terms && (
                <div className={classes.block}>
                    <div className={classes.blockHeading}>Terms and Conditions:</div>
                    <div className={classes.blockBody}>
                        <ClauseText text={p.terms} heading="Terms and Conditions" />
                    </div>
                </div>
            )}

            {p.notes && (
                <div className={classes.blockPlain}>
                    <div className={classes.blockHeading}>Notes:</div>
                    <div className={classes.blockBody}>
                        <ClauseText text={p.notes} heading="Notes" />
                    </div>
                </div>
            )}

            {/*
              * The signature block, mirroring the renderer: a facsimile above "Authorised
              * Signatory", which is the convention on an Indian commercial document and not a
              * claim to be an electronic signature. Nothing at all when the brand has neither a
              * signature nor a signatory named, rather than an empty box implying a withheld mark.
              */}
            {(signatureQuery.data?.url || org?.signatoryName) && (
                <div className={classes.signatureBlock}>
                    <div className={classes.signatureFor}>For {org?.name ?? '—'}</div>
                    {signatureQuery.data?.url ? (
                        <img
                            src={signatureQuery.data.url}
                            alt=""
                            className={classes.signatureMark}
                        />
                    ) : (
                        <div className={classes.signatureSpace} />
                    )}
                    {org?.signatoryName && (
                        <div className={classes.signatureName}>{org.signatoryName}</div>
                    )}
                    <div className={classes.label}>
                        {org?.signatoryDesignation || 'Authorised Signatory'}
                    </div>
                </div>
            )}

            <div className={classes.foot}>
                Please confirm receipt of this order in the Reparo portal.
            </div>
        </div>
    );
};

export default ProductionOrderPreview;
